import React, { useState, useCallback, useMemo } from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  Button,
  Stack,
  Paper,
  Link,
  CardContent,
  useTheme,
  IconButton,
  Tooltip,
  Menu,
  MenuItem
} from '@mui/material';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { DownloadOutlined } from '@ant-design/icons';
import exportGetWhoToBuyDocx from 'utils/exportGetWhoToBuyDocx';
import exportGetWhoToBuyPptx from 'utils/exportGetWhoToBuyPptx';
import exportGetWhoToBuyPptx2 from 'utils/exportGetWhoToBuyPptx2';
import exportGetWhoToBuyPdf from 'utils/exportGetWhoToBuyPdf';
import {
  People as UsersIcon,
  GpsFixed as TargetIcon,
  Bolt as BoltIcon,
  AutoAwesome as SparklesIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon
} from '@mui/icons-material';

/**
 * Dedicated report viewer for Get Who To Buy Strategy Tool
 * Renders the GET/WHO/TO/BY strategic framework
 */
const GetWhoToBuyReportViewer = ({ reportData }) => {
  const theme = useTheme();
  const renderId = React.useRef(0);
  renderId.current += 1;
  const currentRenderId = renderId.current;

  console.log(`🔄 [RENDER #${currentRenderId}] GetWhoToBuyReportViewer - START`);
  console.log(`   📦 reportData changed:`, reportData !== undefined);

  const containerRef = React.useRef(null);
  const [isExpanded, setIsExpanded] = useState(true);
  const [downloadAnchorEl, setDownloadAnchorEl] = useState(null);

  console.log(`   📊 isExpanded state:`, isExpanded);

  const handleToggleExpand = React.useCallback(() => {
    console.log(`🔘 [RENDER #${currentRenderId}] Toggle clicked`);
    console.log(`   📊 Current isExpanded:`, isExpanded);
    console.log(`   📊 Will set to:`, !isExpanded);
    setIsExpanded((prev) => {
      console.log(`   ⚡ setState called, prev:`, prev, `new:`, !prev);
      return !prev;
    });
  }, [isExpanded, currentRenderId]);

  console.log(`   🔧 handleToggleExpand recreated:`, handleToggleExpand);

  // Extract strategy data from reportData
  const strategyData = React.useMemo(() => {
    console.log(`   💾 [RENDER #${currentRenderId}] strategyData useMemo recalculating`);
    if (!reportData) return null;

    let toolResult =
      reportData.analysisData ||
      reportData.tool_result ||
      reportData.data?.tool_result ||
      reportData.data ||
      reportData.result?.tool_result ||
      reportData.result ||
      reportData.content?.tool_result ||
      reportData.content ||
      reportData.metadata?.tool_result ||
      reportData.metadata ||
      reportData;

    // Handle case where tool_result is a string (JSON wrapped in markdown code blocks)
    if (typeof toolResult === 'string') {
      try {
        // Remove markdown code fences if present (```json ... ```)
        let jsonString = toolResult.trim();
        if (jsonString.startsWith('```')) {
          // Remove opening ```json or ```
          jsonString = jsonString.replace(/^```(?:json)?\s*\n?/, '');
          // Remove closing ```
          jsonString = jsonString.replace(/\n?```\s*$/, '');
        }
        // Parse the JSON string
        toolResult = JSON.parse(jsonString);
      } catch (e) {
        console.error('Failed to parse tool_result string as JSON:', e);
        // If parsing fails, return null so the component shows "not available" message
        return null;
      }
    }

    if (toolResult?.get || toolResult?.who || toolResult?.to || toolResult?.by || toolResult?.client || toolResult?.category) {
      return toolResult;
    }

    return null;
  }, [reportData]);

  // Helper to render text with clickable links
  // Use stable keys based on content to prevent re-renders
  const renderTextWithLinks = useCallback((text) => {
    console.log(`   🔗 [RENDER #${currentRenderId}] renderTextWithLinks called`);
    if (!text) return null;

    const parts = [];
    let lastIndex = 0;

    // Find all markdown links [text](url) first
    const markdownLinkRegex = /\[([^\]]+)\]\(([^)]+)\)/g;
    const markdownMatches = [];
    let match;

    while ((match = markdownLinkRegex.exec(text)) !== null) {
      markdownMatches.push({
        type: 'markdown',
        start: match.index,
        end: match.index + match[0].length,
        text: match[1],
        url: match[2]
      });
    }

    // Find citation patterns: (Source, Year, URL). (domain.com)
    // We want to make (Source, Year) clickable and hide URL + (domain.com)
    const citationPattern = /\(([^,]+),\s*([^,]+),\s*(https?:\/\/[^\s)]+)\)\.\s*\(([a-zA-Z0-9.-]+\.[a-zA-Z]{2,})\)/g;
    const citationMatches = [];
    const hiddenRanges = new Set();

    while ((match = citationPattern.exec(text)) !== null) {
      const source = match[1];
      const year = match[2];
      const url = match[3];
      const domainRef = match[4];
      const urlDomain = url
        .replace(/^https?:\/\/(www\.)?/, '')
        .split('/')[0]
        .split('?')[0]
        .toLowerCase();
      const refDomain = domainRef.toLowerCase();

      // Check if domains match (accounting for www)
      const domainsMatch = urlDomain === refDomain || urlDomain === `www.${refDomain}` || `www.${urlDomain}` === refDomain;

      if (domainsMatch) {
        const fullMatchStart = match.index;
        const fullMatchEnd = match.index + match[0].length;

        // Find positions: (Source, Year, URL). (domain.com)
        // We want: (Source, Year) to be clickable
        const openingParen = fullMatchStart;
        const firstComma = text.indexOf(',', openingParen);
        const secondComma = text.indexOf(',', firstComma + 1);
        // Find end of year (after year, before the comma before URL)
        const yearEnd = secondComma;

        // Citation text: (Source, Year) - construct from extracted values
        const citationText = `(${source}, ${year})`;
        citationMatches.push({
          type: 'citation',
          start: openingParen,
          end: yearEnd,
          text: citationText,
          url: url
        });

        // Mark ranges to hide: ", URL). (domain.com)" - everything after the year
        for (let i = yearEnd; i < fullMatchEnd; i++) {
          hiddenRanges.add(i);
        }
      }
    }

    // Find plain URLs (excluding those in citations and markdown links)
    const urlRegex = /(https?:\/\/[^\s)]+)/g;
    const urlMatches = [];

    while ((match = urlRegex.exec(text)) !== null) {
      const urlStart = match.index;
      const urlEnd = match.index + match[0].length;
      const url = match[0];

      // Check if URL is inside a markdown link
      const isInMarkdown = markdownMatches.some((m) => urlStart >= m.start && urlEnd <= m.end);

      // Check if URL is in a hidden range (citation)
      let isHidden = false;
      for (let i = urlStart; i < urlEnd; i++) {
        if (hiddenRanges.has(i)) {
          isHidden = true;
          break;
        }
      }

      if (!isInMarkdown && !isHidden) {
        urlMatches.push({
          type: 'url',
          start: urlStart,
          end: urlEnd,
          url: url
        });
      }
    }

    // Combine all matches (exclude URLs that should be hidden)
    const allMatches = [...markdownMatches, ...citationMatches, ...urlMatches].sort((a, b) => a.start - b.start);

    // Build parts array, skipping hidden ranges
    allMatches.forEach((match) => {
      if (match.start > lastIndex) {
        // Add text before match, skipping hidden ranges
        let textBefore = '';
        for (let i = lastIndex; i < match.start; i++) {
          if (!hiddenRanges.has(i)) {
            textBefore += text[i];
          }
        }
        if (textBefore) {
          parts.push({
            type: 'text',
            content: textBefore
          });
        }
      }
      parts.push(match);
      lastIndex = match.end;
    });

    // Add remaining text, skipping hidden ranges
    if (lastIndex < text.length) {
      let remainingText = '';
      for (let i = lastIndex; i < text.length; i++) {
        if (!hiddenRanges.has(i)) {
          remainingText += text[i];
        }
      }
      if (remainingText) {
        parts.push({
          type: 'text',
          content: remainingText
        });
      }
    }

    // Render with stable keys based on content position
    return parts.map((part, idx) => {
      const stableKey = `${part.type}-${part.start || idx}-${part.end || idx}`;
      if (part.type === 'text') {
        return <span key={stableKey}>{part.content}</span>;
      } else if (part.type === 'markdown') {
        return (
          <Link
            key={stableKey}
            href={part.url}
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              wordBreak: 'break-word',
              color: 'primary.main',
              textDecoration: 'none',
              '&:hover': { textDecoration: 'underline' }
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {part.text}
          </Link>
        );
      } else if (part.type === 'citation') {
        return (
          <Link
            key={stableKey}
            href={part.url}
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              wordBreak: 'break-word',
              color: 'primary.main',
              textDecoration: 'none',
              '&:hover': { textDecoration: 'underline' }
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {part.text}
          </Link>
        );
      } else if (part.type === 'url') {
        return (
          <Link
            key={stableKey}
            href={part.url}
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              wordBreak: 'break-word',
              color: 'primary.main',
              textDecoration: 'none',
              '&:hover': { textDecoration: 'underline' }
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {part.url}
          </Link>
        );
      }
      return null;
    });
  }, []);

  // Citation highlighting and scrolling functionality
  React.useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const clearExternalHighlights = () => {
      if (!container) return;
      const nodes = container.querySelectorAll('.external-citation-highlight');
      nodes.forEach((node) => {
        const parent = node.parentNode;
        if (!parent) return;
        parent.replaceChild(document.createTextNode(node.textContent), node);
        parent.normalize();
      });
    };

    const applyExternalHighlight = (text) => {
      if (!container || !text) return false;
      const base = String(text).trim();
      if (!base) return false;
      const ensureEllipsis = (s) => (/[.…]$/.test(s) || /\.\.\.$/.test(s) ? s : `${s}…`);
      const swapDots = (s) => s.replace(/\.\.\./g, '…');
      const swapEllipsis = (s) => s.replace(/…/g, '...');
      const stripQuotes = (s) => s.replace(/[""]/g, '"').replace(/['']/g, "'");
      const variants = Array.from(
        new Set([
          base,
          ensureEllipsis(base),
          swapDots(base),
          swapDots(ensureEllipsis(base)),
          swapEllipsis(base),
          stripQuotes(base),
          stripQuotes(ensureEllipsis(base))
        ])
      );
      const walker = document.createTreeWalker(container, NodeFilter.SHOW_TEXT, null, false);
      let node;
      while ((node = walker.nextNode())) {
        const content = node.textContent || '';
        const contentLower = content.toLowerCase();
        let foundStart = -1;
        let foundLen = 0;
        for (const v of variants) {
          const vLower = String(v).toLowerCase();
          const i = contentLower.indexOf(vLower);
          if (i !== -1) {
            foundStart = i;
            foundLen = vLower.length;
            break;
          }
        }
        if (foundStart !== -1) {
          try {
            const range = document.createRange();
            range.setStart(node, foundStart);
            range.setEnd(node, foundStart + foundLen);
            const span = document.createElement('span');
            span.className = 'external-citation-highlight';
            span.style.backgroundColor = 'rgba(255, 215, 0, 0.35)';
            span.style.borderRadius = '2px';
            span.style.padding = '1px 2px';
            try {
              range.surroundContents(span);
            } catch (e) {
              span.appendChild(range.extractContents());
              range.insertNode(span);
            }
            setTimeout(() => {
              try {
                span.scrollIntoView({ behavior: 'smooth', block: 'center' });
              } catch (e) {
                /* ignore */
              }
            }, 0);
            return true;
          } catch (e) {
            // continue scanning
          }
        }
      }
      return false;
    };

    const onHighlight = (evt) => {
      const text = evt && evt.detail ? evt.detail.text : '';
      if (!text) return;
      clearExternalHighlights();
      if (!applyExternalHighlight(text)) {
        const stripped = String(text)
          .replace(/["'""]/g, '')
          .trim();
        if (stripped) applyExternalHighlight(stripped);
      }
    };

    const onClear = () => clearExternalHighlights();

    window.addEventListener('get-who-to-buy:highlight', onHighlight);
    window.addEventListener('get-who-to-buy:clear-highlight', onClear);
    return () => {
      window.removeEventListener('get-who-to-buy:highlight', onHighlight);
      window.removeEventListener('get-who-to-buy:clear-highlight', onClear);
      clearExternalHighlights();
    };
  }, []);

  // Memoize insights content to prevent re-renders - must be before early return
  const insightsContent = useMemo(() => {
    console.log(`   💾 [RENDER #${currentRenderId}] insightsContent useMemo recalculating`);
    if (!strategyData) return null;

    return (
      <Stack spacing={5}>
        {/* Key Strategic Insight */}
        {strategyData.key_strategic_insight && (
          <Box>
            <Typography
              variant="subtitle1"
              sx={{
                fontWeight: 700,
                mb: 1,
                color: 'primary.dark',
                letterSpacing: '0.02em',
                textTransform: 'uppercase',
                fontSize: '0.75rem'
              }}
            >
              Key Strategic Insight
            </Typography>
            <Typography variant="body1" sx={{ fontSize: '1.1rem', lineHeight: 1.7, color: 'text.primary', fontWeight: 500 }}>
              {renderTextWithLinks(strategyData.key_strategic_insight)}
            </Typography>
          </Box>
        )}

        {/* Creative Implication */}
        {strategyData.creative_implication && (
          <Box>
            <Typography
              variant="subtitle1"
              sx={{
                fontWeight: 700,
                mb: 1,
                color: 'primary.dark',
                letterSpacing: '0.02em',
                textTransform: 'uppercase',
                fontSize: '0.75rem'
              }}
            >
              Creative Implication
            </Typography>
            <Typography variant="body1" sx={{ fontSize: '1rem', lineHeight: 1.7, color: 'text.primary' }}>
              {renderTextWithLinks(strategyData.creative_implication)}
            </Typography>
          </Box>
        )}

        {/* Supporting Data */}
        {strategyData.supporting_data_points && strategyData.supporting_data_points.length > 0 && (
          <Box>
            <Typography
              variant="subtitle1"
              sx={{
                fontWeight: 700,
                mb: 2,
                color: 'primary.dark',
                letterSpacing: '0.02em',
                textTransform: 'uppercase',
                fontSize: '0.75rem'
              }}
            >
              Supporting Data Points
            </Typography>
            <Stack spacing={2}>
              {strategyData.supporting_data_points.map((point, index) => (
                <Stack key={`point-${index}`} direction="row" spacing={2} alignItems="flex-start">
                  <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: 'primary.main', mt: 1, flexShrink: 0 }} />
                  <Typography variant="body2" sx={{ fontSize: '0.95rem', lineHeight: 1.6, color: 'text.primary' }}>
                    {renderTextWithLinks(point)}
                  </Typography>
                </Stack>
              ))}
            </Stack>
          </Box>
        )}

        {/* Tone & Channels */}
        {strategyData.tone_channel_direction && (
          <Box>
            <Typography
              variant="subtitle1"
              sx={{
                fontWeight: 700,
                mb: 2,
                color: 'primary.dark',
                letterSpacing: '0.02em',
                textTransform: 'uppercase',
                fontSize: '0.75rem'
              }}
            >
              Tone & Channel Direction
            </Typography>
            <Stack spacing={2}>
              {Object.entries(strategyData.tone_channel_direction).map(([key, value]) => {
                if (!value) return null;
                const label = key
                  .split('_')
                  .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
                  .join(' ');
                return (
                  <Typography key={`tone-${key}`} variant="body2" sx={{ fontSize: '0.95rem', lineHeight: 1.6, color: 'text.primary' }}>
                    <Box component="strong" sx={{ color: 'text.primary', fontWeight: 600 }}>
                      {label}:
                    </Box>{' '}
                    {renderTextWithLinks(value)}
                  </Typography>
                );
              })}
            </Stack>
          </Box>
        )}

        {/* Strategic Guardrails */}
        {strategyData.strategic_guardrails && (
          <Box>
            <Typography
              variant="subtitle1"
              sx={{
                fontWeight: 700,
                mb: 2,
                color: 'primary.dark',
                letterSpacing: '0.02em',
                textTransform: 'uppercase',
                fontSize: '0.75rem'
              }}
            >
              Strategic Guardrails
            </Typography>
            <Stack spacing={2}>
              {Object.entries(strategyData.strategic_guardrails).map(([key, value]) => {
                if (!value) return null;
                const label = key
                  .split('_')
                  .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
                  .join(' ');
                return (
                  <Typography key={`guardrail-${key}`} variant="body2" sx={{ fontSize: '0.95rem', lineHeight: 1.6, color: 'text.primary' }}>
                    <Box component="strong" sx={{ color: 'text.primary', fontWeight: 600 }}>
                      {label}:
                    </Box>{' '}
                    {renderTextWithLinks(value)}
                  </Typography>
                );
              })}
            </Stack>
          </Box>
        )}
      </Stack>
    );
  }, [
    strategyData?.key_strategic_insight,
    strategyData?.creative_implication,
    strategyData?.supporting_data_points,
    strategyData?.tone_channel_direction,
    strategyData?.strategic_guardrails,
    renderTextWithLinks
  ]);

  // Memoize sections to prevent recreation on every render - must be before early return
  const sections = useMemo(() => {
    console.log(`   📋 [RENDER #${currentRenderId}] sections useMemo recalculating`);
    if (!strategyData) return [];
    return [
      { key: 'get', label: 'Get', icon: UsersIcon, content: strategyData.get, color: 'primary' },
      { key: 'who', label: 'Who', icon: TargetIcon, content: strategyData.who, color: 'secondary' },
      { key: 'to', label: 'To', icon: BoltIcon, content: strategyData.to, color: 'warning' },
      { key: 'by', label: 'By', icon: SparklesIcon, content: strategyData.by, color: 'success' }
    ];
  }, [strategyData?.get, strategyData?.who, strategyData?.to, strategyData?.by]);

  // Memoize rendered section content - must be before early return
  const renderedSections = useMemo(() => {
    console.log(`   📝 [RENDER #${currentRenderId}] renderedSections useMemo recalculating`);
    if (!sections.length) return [];
    return sections.map((section) => ({
      ...section,
      renderedContent: renderTextWithLinks(section.content)
    }));
  }, [sections, renderTextWithLinks]);

  // Memoize supporting text paragraphs - must be before early return
  const supportingTextParagraphs = useMemo(() => {
    console.log(`   📄 [RENDER #${currentRenderId}] supportingTextParagraphs useMemo recalculating`);
    if (!strategyData?.supporting_text) return null;
    const paragraphs = strategyData.supporting_text.split(/\n\n+/);
    return paragraphs
      .map((paragraph, index) => {
        const trimmed = paragraph.trim();
        if (!trimmed) return null;
        return {
          key: index,
          content: trimmed, // Store raw string, ReactMarkdown will handle rendering
          isLast: index === paragraphs.length - 1
        };
      })
      .filter(Boolean);
  }, [strategyData?.supporting_text]);

  if (!strategyData) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="body2" color="text.primary">
          Strategy framework data not available.
        </Typography>
      </Box>
    );
  }

  console.log(`   ✅ [RENDER #${currentRenderId}] About to return JSX`);
  console.log(`   📊 Final isExpanded:`, isExpanded);

  return (
    <Container maxWidth="md" sx={{ py: 4 }} ref={containerRef}>
      {/* Header */}
      <Box sx={{ mb: 5, textAlign: 'center', position: 'relative' }}>
        <Stack direction="row" spacing={1} alignItems="center" justifyContent="center" sx={{ mb: 1 }}>
          <SparklesIcon sx={{ color: 'primary.main', fontSize: 28 }} />
          <Typography variant="h3" sx={{ fontWeight: 700 }}>
            Strategic Framework
          </Typography>
        </Stack>
        {strategyData.client && (
          <Typography variant="h5" color="text.primary" sx={{ fontWeight: 500 }}>
            for {strategyData.client}
          </Typography>
        )}
        {/* Download Button with Menu */}
        <Tooltip title="Download report">
          <IconButton
            onClick={(e) => setDownloadAnchorEl(e.currentTarget)}
            sx={{
              position: 'absolute',
              top: 0,
              right: 0,
              color: 'text.secondary',
              '&:hover': {
                color: 'primary.main',
                bgcolor: 'action.hover'
              }
            }}
          >
            <DownloadOutlined />
          </IconButton>
        </Tooltip>
        <Menu anchorEl={downloadAnchorEl} open={Boolean(downloadAnchorEl)} onClose={() => setDownloadAnchorEl(null)}>
          <MenuItem
            onClick={() => {
              const clientName = strategyData.client ? strategyData.client.replace(/\s+/g, '_') : 'strategy';
              const fileName = `get-who-to-buy-strategy-${clientName}.docx`;
              exportGetWhoToBuyDocx(strategyData, fileName);
              setDownloadAnchorEl(null);
            }}
          >
            Download as .docx
          </MenuItem>
          <MenuItem
            onClick={() => {
              const clientName = strategyData.client ? strategyData.client.replace(/\s+/g, '_') : 'strategy';
              const fileName = `get-who-to-buy-strategy-${clientName}.pptx`;
              exportGetWhoToBuyPptx(strategyData, fileName);
              setDownloadAnchorEl(null);
            }}
          >
            Download as .pptx
          </MenuItem>
          <MenuItem
            onClick={() => {
              const clientName = strategyData.client ? strategyData.client.replace(/\s+/g, '_') : 'strategy';
              const fileName = `get-who-to-buy-strategy-${clientName}-v2.pptx`;
              exportGetWhoToBuyPptx2(strategyData, fileName);
              setDownloadAnchorEl(null);
            }}
          >
            Download as .pptx (Experimental)
          </MenuItem>
          <MenuItem
            onClick={() => {
              const clientName = strategyData.client ? strategyData.client.replace(/\s+/g, '_') : 'strategy';
              const fileName = `get-who-to-buy-strategy-${clientName}.pdf`;
              exportGetWhoToBuyPdf(strategyData, fileName);
              setDownloadAnchorEl(null);
            }}
          >
            Download as .pdf
          </MenuItem>
        </Menu>
      </Box>

      {/* Main Framework Card - Designer Quality */}
      <Card
        elevation={0}
        sx={{
          border: 0,
          background: 'linear-gradient(135deg, #1e88e5 0%, #1565c0 100%)', // Classic blue gradient
          borderRadius: 4,
          overflow: 'hidden',
          mb: 4,
          boxShadow: '0 24px 48px -12px rgba(21, 101, 192, 0.25)'
        }}
      >
        <Box sx={{ p: { xs: 3, md: 6 }, position: 'relative', bgcolor: 'transparent' }}>
          <Stack spacing={5}>
            {renderedSections.map((section) => (
              <Box key={section.key} sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '140px 1fr' }, gap: 3 }}>
                {/* Icon & Title */}
                <Stack direction="row" spacing={2} alignItems="flex-start">
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: 48,
                      height: 48,
                      borderRadius: 3,
                      bgcolor: 'rgba(255,255,255,0.15)',
                      backdropFilter: 'blur(8px)',
                      flexShrink: 0,
                      border: '1px solid rgba(255,255,255,0.2)'
                    }}
                  >
                    <section.icon sx={{ fontSize: 24, color: 'white' }} />
                  </Box>
                  <Typography
                    variant="h3"
                    sx={{
                      fontWeight: 800,
                      color: 'white',
                      fontSize: '2.25rem',
                      lineHeight: 1,
                      letterSpacing: '-0.02em'
                    }}
                  >
                    {section.label}
                  </Typography>
                </Stack>

                {/* Content */}
                <Paper
                  elevation={0}
                  sx={{
                    p: 3,
                    borderRadius: 3,
                    bgcolor: 'white',
                    boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: '0 6px 24px rgba(0,0,0,0.12)',
                      bgcolor: 'grey.50'
                    }
                  }}
                >
                  <Typography
                    variant="body1"
                    sx={{
                      lineHeight: 1.6,
                      fontSize: '0.95rem', // Slightly smaller as requested
                      color: 'text.primary',
                      fontWeight: 500
                    }}
                  >
                    {section.renderedContent}
                  </Typography>
                </Paper>
              </Box>
            ))}
          </Stack>

          <Box sx={{ mt: 6 }}>
            <Button
              variant="text"
              onClick={handleToggleExpand}
              fullWidth
              sx={{
                color: 'white',
                justifyContent: 'center',
                py: 1.5,
                borderRadius: 2,
                textTransform: 'none',
                fontSize: '0.95rem',
                fontWeight: 600,
                bgcolor: 'rgba(255,255,255,0.1)',
                '&:hover': {
                  bgcolor: 'rgba(255,255,255,0.2)',
                  color: 'white'
                }
              }}
              startIcon={isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            >
              {isExpanded ? 'Hide Strategic Insights' : 'Show Strategic Insights'}
            </Button>

            {/* Custom height animation - no opacity to prevent blue showing through */}
            <Box
              sx={{
                mt: 3,
                bgcolor: 'white',
                backgroundColor: 'white',
                borderRadius: 3,
                overflow: 'hidden',
                height: isExpanded ? 'auto' : 0,
                maxHeight: isExpanded ? '5000px' : '0px',
                transition: 'max-height 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                // Ensure white background is always solid
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  bgcolor: 'white',
                  backgroundColor: 'white',
                  zIndex: -1
                }
              }}
            >
              <Box sx={{ bgcolor: 'white', backgroundColor: 'white' }}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 4,
                    borderRadius: 0,
                    bgcolor: 'white',
                    backgroundColor: 'white',
                    m: 0
                  }}
                >
                  {insightsContent}
                </Paper>
              </Box>
            </Box>
          </Box>
        </Box>
      </Card>

      {/* Supporting Text and Notes Below Card - Single Chat Message */}
      {(supportingTextParagraphs || strategyData.notes_data_recency) && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-start',
            width: '100%',
            mt: 2
          }}
        >
          <Card
            sx={{
              maxWidth: '100%',
              width: '100%',
              background: theme.palette.grey[100],
              color: theme.palette.text.primary,
              borderRadius: '20px 20px 20px 4px',
              boxShadow: `0 2px 10px ${theme.palette.action.hover}`
            }}
          >
            <CardContent
              sx={{
                px: 2,
                py: 2,
                '&:last-child': { pb: 2 },
                '& h1, & h2, & h3, & h4, & h5, & h6': {
                  mt: 2,
                  mb: 1,
                  fontWeight: 600,
                  '&:first-of-type': {
                    mt: 0
                  }
                },
                '& h1': { fontSize: '1.4rem' },
                '& h2': { fontSize: '1.2rem' },
                '& h3': { fontSize: '1.05rem' },
                '& p': {
                  mb: 1,
                  lineHeight: 1.6,
                  fontSize: '0.9rem',
                  '&:last-child': {
                    mb: 0
                  }
                },
                '& a': {
                  color: theme.palette.primary.main,
                  textDecoration: 'underline',
                  '&:hover': {
                    opacity: 0.8
                  }
                },
                '& ul, & ol': {
                  pl: 3,
                  mb: 1
                },
                '& li': {
                  mb: 0.5
                },
                '& code': {
                  backgroundColor: 'rgba(0,0,0,0.05)',
                  padding: '2px 6px',
                  borderRadius: '4px',
                  fontSize: '0.875rem',
                  fontFamily: 'monospace'
                },
                '& pre': {
                  backgroundColor: 'rgba(0,0,0,0.05)',
                  padding: '12px',
                  borderRadius: '8px',
                  overflow: 'auto',
                  mb: 1
                },
                '& pre code': {
                  backgroundColor: 'transparent',
                  padding: 0
                },
                '& blockquote': {
                  borderLeft: `4px solid ${theme.palette.grey[400]}`,
                  pl: 2,
                  ml: 0,
                  my: 1,
                  fontStyle: 'italic',
                  opacity: 0.9
                },
                '& strong': {
                  fontWeight: 600
                },
                fontSize: '0.9rem'
              }}
            >
              <ReactMarkdown remarkPlugins={[remarkGfm]}>
                {(() => {
                  const parts = [];

                  // Add supporting text paragraphs with titles
                  if (supportingTextParagraphs && supportingTextParagraphs.length > 0) {
                    supportingTextParagraphs.forEach((para, index) => {
                      const title = index === 0 ? '## Context' : index === 1 ? '## Value Proposition' : '## Framework Application';
                      parts.push(`${title}\n\n${para.content}`);
                    });
                  }

                  // Add note as blockquote
                  if (strategyData.notes_data_recency) {
                    parts.push(`> **Note:** ${strategyData.notes_data_recency}`);
                  }

                  return parts.join('\n\n');
                })()}
              </ReactMarkdown>
            </CardContent>
          </Card>
        </Box>
      )}
    </Container>
  );
};

// Wrap with React.memo to track parent re-renders
const MemoizedGetWhoToBuyReportViewer = React.memo(GetWhoToBuyReportViewer, (prevProps, nextProps) => {
  const propsChanged = prevProps.reportData !== nextProps.reportData;
  console.log('🔍 React.memo comparison:', { propsChanged });
  return !propsChanged; // Only re-render if props actually changed
});

export default MemoizedGetWhoToBuyReportViewer;
